'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Per-user step progress for the Tying Bench preview. Persisted to
 * localStorage under `progressKey` (in production this would move to the
 * existing Supabase likes/bookmarks pattern). Step keys are `${group}:${step}`.
 */
export function useBenchProgress(progressKey: string) {
  const [doneSteps, setDoneSteps] = useState<Set<string>>(new Set())
  const ready = useRef(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(progressKey)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- restore progress from localStorage after mount (render-time read would mismatch SSR)
      if (raw) setDoneSteps(new Set(JSON.parse(raw) as string[]))
    } catch {
      /* ignore */
    }
    ready.current = true
  }, [progressKey])

  useEffect(() => {
    if (!ready.current) return
    try {
      localStorage.setItem(progressKey, JSON.stringify([...doneSteps]))
    } catch {
      /* ignore */
    }
  }, [doneSteps, progressKey])

  const toggleStep = useCallback((key: string) => {
    setDoneSteps((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  return { doneSteps, toggleStep }
}
